import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ApprovalState, ApprovalType, ContentChannel, SocialPlatform } from '@mg/shared';
import type { CalendarItem } from '@mg/shared';
import { cn } from '../../lib/cn';
import { channelLabel } from '../../lib/display';
import { useEffectiveApprovalType } from '../../lib/useEffectiveApprovalType';
import { useTenantKey } from '../../lib/useTenantKey';
import { PostsCalendar } from './PostsCalendar';
import { NON_SOCIAL_PLATFORMS, POST_TYPE_ORDER, postTypeIcon } from './postTypes';
import { PLATFORM_META } from '../social/platforms';
import { SUBHEADER_CLS, TAB_ORDER, TAB_STATES, type StatusTabId } from '../approval/statusTabs';

/** Post-type / status selections default to "all" (a calendar wants everything, not one bucket). */
type TypeSel = ContentChannel | 'all';
type StatusSel = StatusTabId | 'all';

/** Social platforms surfaced as filter chips, in Figma order. */
const CHIP_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.LINKEDIN,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.THREADS,
  SocialPlatform.X,
];

/** No PODCAST: it is not a filter any more (`POST_TYPE_ORDER`), so a bookmarked ?ctype=PODCAST opens on All. */
function parseTypeSel(value: string | null): TypeSel {
  return value === ContentChannel.BLOG ||
    value === ContentChannel.SOCIAL ||
    value === ContentChannel.VIDEO
    ? value
    : 'all';
}
function parseStatusSel(value: string | null): StatusSel {
  return value && TAB_ORDER.some((t) => t.id === value) ? (value as StatusTabId) : 'all';
}
function platformOptionsFor(type: TypeSel): string[] {
  if (type === ContentChannel.SOCIAL) return CHIP_PLATFORMS;
  if (type === ContentChannel.BLOG) return NON_SOCIAL_PLATFORMS.BLOG;
  if (type === ContentChannel.VIDEO) return NON_SOCIAL_PLATFORMS.VIDEO;
  return [];
}
function platformOptionLabel(type: TypeSel, id: string): string {
  return type === ContentChannel.SOCIAL ? PLATFORM_META[id as SocialPlatform].label : id;
}

/**
 * The state a calendar item is BUCKETED by for the Status filter — mirrors PostsView's
 * `postBadgeState` so the calendar and the All-Posts tab agree: POST mode uses the post's own
 * state; NONE mode has no approval to advance, so a scheduled/published entry reads SCHEDULED/
 * PUBLISHED (its campaign never leaves DRAFT); CAMPAIGN mode inherits the campaign's aggregate.
 */
function calBadgeState(item: CalendarItem, approvalType: ApprovalType | undefined): ApprovalState {
  if (approvalType === ApprovalType.POST && item.postApprovalState) return item.postApprovalState;
  if (approvalType === ApprovalType.NONE) {
    return item.publishedAt
      ? ApprovalState.PUBLISHED
      : item.scheduledAt
        ? ApprovalState.SCHEDULED
        : item.approvalState;
  }
  return item.approvalState;
}

/**
 * The "Postings" sub-tab of the Calendar: the month-grid calendar plus Status / Post Type /
 * Platform filter chips. Selections live in the URL (`cstatus`/`ctype`/`cplatform`, distinct from
 * the All-Posts tab's `tab`/`type`/`platform` so the two views don't fight) and default to "all".
 * Filtering is client-side over the month's already-fetched calendar items.
 */
export function CalendarView() {
  const tenantKey = useTenantKey();
  const approvalType = useEffectiveApprovalType();
  const [params, setParams] = useSearchParams();
  const status = parseStatusSel(params.get('cstatus'));
  const type = parseTypeSel(params.get('ctype'));
  const platform = params.get('cplatform');

  const patch = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const filterFn = useMemo(() => {
    return (item: CalendarItem): boolean => {
      if (status !== 'all' && !TAB_STATES[status].includes(calBadgeState(item, approvalType)))
        return false;
      if (type !== 'all' && item.channel !== type) return false;
      // Platform is a type-dependent chip (its options come from the selected type), so it only
      // filters when a specific type is chosen — otherwise a stray ?cplatform= would empty the view.
      if (type !== 'all' && platform) {
        if (type === ContentChannel.SOCIAL) {
          if (item.platform !== platform) return false;
        } else if (item.platformLabel !== platform) {
          return false;
        }
      }
      return true;
    };
  }, [status, type, platform, approvalType]);

  const platformOptions = platformOptionsFor(type);

  return (
    <>
      <div className="mb-4">
        <p className={SUBHEADER_CLS}>Status</p>
        <div className="flex flex-wrap items-center gap-2">
          <Chip active={status === 'all'} onClick={() => patch({ cstatus: null })} label="All" />
          {TAB_ORDER.map((t) => (
            <Chip
              key={t.id}
              active={status === t.id}
              onClick={() => patch({ cstatus: t.id })}
              label={t.label}
            />
          ))}
        </div>
      </div>

      <div className="mb-4">
        <p className={SUBHEADER_CLS}>Post Type</p>
        <div className="flex flex-wrap items-center gap-2">
          <Chip
            active={type === 'all'}
            onClick={() => patch({ ctype: null, cplatform: null })}
            label="All"
          />
          {POST_TYPE_ORDER.map((c) => (
            <Chip
              key={c}
              active={type === c}
              // Changing the Post Type resets the (type-dependent) Platform filter.
              onClick={() => patch({ ctype: c, cplatform: null })}
              icon={postTypeIcon[c]}
              label={channelLabel[c]}
            />
          ))}
        </div>
      </div>

      <div className="mb-5">
        <p className={SUBHEADER_CLS}>Platform</p>
        <div className="flex flex-wrap items-center gap-2">
          <Chip active={!platform} onClick={() => patch({ cplatform: null })} label="All" />
          {platformOptions.map((id) => (
            <Chip
              key={id}
              active={platform === id}
              onClick={() => patch({ cplatform: id })}
              label={platformOptionLabel(type, id)}
            />
          ))}
        </div>
      </div>

      <PostsCalendar tenantKey={tenantKey} filterFn={filterFn} />
    </>
  );
}

function Chip({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'focus-ring inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-action text-action-fg'
          : 'border border-line-input bg-card text-ink-strong hover:bg-hover',
      )}
      aria-pressed={active}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {label}
    </button>
  );
}
